import axios from "axios";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Toast } from "../Marketing/Common/Toast";

interface Department {
  DepartmentId: number;
  DepartmentName: string;
}
interface Location {
  LocationId: number;
  LocationName: string;
  HospitalName?: string;
}
interface SubmittedData {
  name: string;
  email:string;
  mobile: string;
  locationName?: string;
  remark?: string;
}

interface NoDoctorsContactFormProps {
  selectedLocations: string[];
  filters: {
    speciality?: string;
  };
  locationId?: number;
}

export const NoDoctorsContactForm = ({
  selectedLocations,
  filters,
  locationId,
}: NoDoctorsContactFormProps) => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [formData, setFormData] = useState({
    fullName: "",
    email:"",
    phoneNumber: "",
    countryCode: "+91",
    remark: "",
  });
  const [isAgreed, setIsAgreed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedSpecializationId, setSelectedSpecializationId] = useState<number | null>(null);
const [locations, setLocations] = useState<Location[]>([]);
const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
const [selectedLocationName, setSelectedLocationName] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isLocationManuallyChanged, setIsLocationManuallyChanged] = useState(false);
const [emailError, setEmailError] = useState("");
  const [submittedData, setSubmittedData] = useState<SubmittedData>({
    name: "",
    email:"",
    mobile: "",
    locationName: "",
    remark: "",
  });

  const source = /Android|iPhone/i.test(navigator.userAgent)
    ? "OAS-Website-Mobile"
    : "OAS-Website-Desktop";

  // ── Derived values ─────────────────────────────────────────────
const specializationId = selectedSpecializationId || 0;
const effectiveLocationName = selectedLocationName || "";
const effectiveLocationId = selectedLocationId || undefined;

const requestFor =
  effectiveLocationName || specializationId > 0 ? "DL" : "AB";
const isABRequest = requestFor === "AB";

const hospitalLocation = effectiveLocationName || "our hospital";

  const selectedSpecialty =
    filters.speciality && filters.speciality !== "All Specialities"
      ? filters.speciality
      : "";


const validateEmail = (email: string): boolean => {
  // Email is optional
  if (!email.trim()) {
    setEmailError("");
    return true;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailRegex.test(email)) {
    setEmailError("Please enter a valid email address");
    return false;
  }

  setEmailError("");
  return true;
};


const toTitleCase = (name: string): string => {
  return name
    .trim()
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map(
      (word) => word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(" ");
};
const formattedName = toTitleCase(formData.fullName);
  // ── Handlers ───────────────────────────────────────────────────
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
useEffect(() => {
  const fetchLocations = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/Patient/locations`
      );

      const locationData = Array.isArray(response?.data) ? response.data : [];
      setLocations(locationData);

      if (isLocationManuallyChanged) return;

      if (locationId) {
        const matched = locationData.find(
          (loc: Location) => loc.LocationId === locationId
        );

        if (matched) {
          setSelectedLocationId(matched.LocationId);
          setSelectedLocationName(matched.LocationName);
          return;
        }
      }

      if (selectedLocations?.[0]) {
        const matched = locationData.find(
          (loc: Location) =>
            loc.LocationName?.toLowerCase() === selectedLocations[0].toLowerCase()
        );

        if (matched) {
          setSelectedLocationId(matched.LocationId);
          setSelectedLocationName(matched.LocationName);
          return;
        }

        setSelectedLocationId(null);
        setSelectedLocationName(selectedLocations[0]);
      }
    } catch (error) {
      console.error("Failed to fetch locations:", error);
    }
  };

  fetchLocations();
}, [locationId, selectedLocations, isLocationManuallyChanged]);
  // ── Fetch departments ──────────────────────────────────────────
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response: any = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/Lookup/departments`,
          {
            params: {
              Remark: "Get the list of the departments",
              Event: "Get Departments",
            },
          }
        );
        if (response?.data) {
          setDepartments(response.data);
          if (filters.speciality && filters.speciality !== "All Specialities") {
            const matchedDept = response.data.find(
              (dept: Department) => dept.DepartmentName === filters.speciality
            );
            if (matchedDept) {
              setSelectedSpecializationId(matchedDept.DepartmentId);
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch departments:", error);
      }
    };
    fetchDepartments();
  }, [filters.speciality]);

  // ── Submit ─────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
const isEmailValid = validateEmail(formData.email);

if (!isEmailValid) {
  setIsLoading(false);
  return;
}
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(formData.phoneNumber)) {
      alert("Please enter a valid 10-digit phone number.");
      setIsLoading(false);
      return;
    }
    if (!executeRecaptcha) {
      alert("reCAPTCHA not ready. Please try again.");
      setIsLoading(false);
      return;
    }
    try {
      const recaptchaToken = await executeRecaptcha("callback_request");
      if (!recaptchaToken) {
        alert("Captcha verification failed. Please try again.");
        setIsLoading(false);
        return;
      }
      if (formData.fullName && isAgreed) {
      const apiPayload = {
  name: formattedName,
  emailId:formData.email,
  mobileNo: formData.phoneNumber,
  isConsent: isAgreed,
  recaptchaToken,
  source,
  requestFor,
  ...(specializationId > 0 && { specializationId }),
  ...(effectiveLocationId && { locationId: effectiveLocationId }),
  ...(effectiveLocationName && { locationName: effectiveLocationName }),
  ...(isABRequest &&
    formData.remark.trim() && { remark: formData.remark.trim() }),
};
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/api/Patient/RequestCallBack`,
          apiPayload,
          { headers: { "Content-Type": "application/json" } }
        );
        if (response.status === 200) {
          try {
    await axios.post(
      "https://integration.pqa.salesmax.ai/salesmax/leads?token=7MAcfjLhv7ITPcK_u2Eqxw",
      {
        name: formattedName,
        email: formData.email,
        mobile: formData.phoneNumber,
        countryCode: formData.countryCode,
        locationId: effectiveLocationId || null,
        locationName: effectiveLocationName || "",
        speciality: selectedSpecialty || "",
        specializationId: specializationId || null,
        remark: formData.remark?.trim() || "",
        source,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  } catch (webhookError) {
    console.error("Salesmax webhook failed:", webhookError);
  }
          setSubmittedData({
            name: formattedName,
            email:formData.email,
            mobile: `${formData.countryCode} ${formData.phoneNumber}`,
            locationName: effectiveLocationName  || undefined,
            remark: isABRequest ? formData.remark || undefined : undefined,
          });

          setShowSuccessModal(true);

          setFormData({ fullName: "", phoneNumber: "", countryCode: "+91", remark: "" ,email:""});
          setIsAgreed(false);
          setSelectedSpecializationId(null);
        } else {
          throw new Error("API error");
        }
      }
    } catch (error: any) {
      console.error("Submission error:", error);
      Toast.error(
        error.response?.data?.message || "Submission failed. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────
  return (
    <>
      {/* ── Success Modal ── */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 transform transition-all animate-fadeIn">
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-10 h-10 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>

            {/* Title */}
            <div className="text-center mb-4">
              <h2 className="text-2xl font-bold text-[#184A84] mb-2">
                Request Submitted Successfully!
              </h2>
              <p className="text-gray-600 text-base">
                Thank you for your request. Our healthcare team will contact you soon.
              </p>
            </div>

            {/* Submitted Details */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <span className="font-semibold text-gray-900 capitalize">
                    {submittedData.name || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Mobile:</span>
                  <span className="font-semibold text-gray-900">
                    {submittedData.mobile || "N/A"}
                  </span>
                </div>
                {selectedSpecialty && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Speciality:</span>
                    <span className="font-semibold text-gray-900">
                      {selectedSpecialty}
                    </span>
                  </div>
                )}
                {submittedData.locationName && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Location:</span>
                    <span className="font-semibold text-gray-900">
                      {submittedData.locationName}
                    </span>
                  </div>
                )}
                {/* {submittedData.remark && (
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-600 shrink-0">Message:</span>
                    <span className="font-semibold text-gray-900 text-right">
                      {submittedData.remark}
                    </span>
                  </div>
                )} */}
              </div>
            </div>

            {/* Close Button */}
            <button
              onClick={() => setShowSuccessModal(false)}
              className="w-full bg-[#009A85] hover:bg-[#007a6a] text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-[1.02] active:scale-95 shadow-lg"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* ── Main Form ── */}
      <div className="w-full max-w-5xl mx-auto bg-white shadow-md rounded-2xl overflow-hidden mt-10">
        {/* Header */}
        <div className="bg-gray-100 px-6 py-5 border-b">
          <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
            <span className="font-semibold">Dear User,</span>
            <br />
            Unfortunately,{" "}
            <span className="font-medium text-[#009A85]">{selectedSpecialty}</span>{" "}
            Doctor is not available at{" "}
            <span className="font-medium text-[#184A84]">{hospitalLocation}</span>{" "}
            at the moment.
            <br />
            Please share your details below. Our{" "}
            <span className="font-medium">health expert</span> will contact you
            to assist with booking.
          </p>
        </div>

        {/* Form Wrapper */}
        <div className="bg-white shadow-md overflow-hidden">
          {/* Form Header */}
          <div className="bg-[#009A85] px-6 py-3">
            <h3 className="text-white font-bold text-lg mb-2 flex items-center gap-3">
              <svg
                className="w-6 h-6 text-white/80"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              Can't find what you are looking for?
            </h3>
            <p className="text-white/90 text-sm">
              Fill in your details and we'll help you find the right doctor
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6 px-6 py-6 bg-gray-50">

            {/* Row: Name + Phone + Submit */}
          <div className="flex flex-col lg:flex-row gap-2 items-stretch">
  {/* Full Name */}
  <div className="flex-1">
    <label
      htmlFor="fullName"
      className="block mb-1 text-sm font-semibold text-gray-700"
    >
      Full Name <span className="text-red-500">*</span>
    </label>
    <input
      id="fullName"
      type="text"
      name="fullName"
      placeholder="Enter Full Name"
      value={formData.fullName}
      onChange={handleInputChange}
      className="w-full text-[13px] px-2 py-2 capitalize rounded-xl border-2 border-gray-200 focus:border-[#184A84] focus:outline-none text-gray-800 bg-white shadow-sm transition-all duration-300"
      required
      disabled={isLoading}
    />
  </div>
  <div className="flex-1">
  <label
    htmlFor="email"
    className="block mb-1 text-sm font-semibold text-gray-700"
  >
    Email Address 
  </label>

  <input
    id="email"
    type="email"
    name="email"
    placeholder="Enter Email Address"
    value={formData.email}
    onChange={handleInputChange}
    className="w-full text-[13px] px-2 py-2  rounded-xl border-2 border-gray-200 focus:border-[#184A84] focus:outline-none text-gray-800 bg-white shadow-sm"
   
    pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
    disabled={isLoading}
  />
</div>

  {/* Phone */}
  <div className="flex-1">
    <label
      htmlFor="phoneNumber"
      className="block mb-1 text-sm font-semibold text-gray-700"
    >
      Phone Number <span className="text-red-500">*</span>
    </label>

    <div className="flex rounded-xl border-2 border-gray-200 bg-white shadow-sm">
      <select
        id="countryCode"
        name="countryCode"
        value={formData.countryCode}
        onChange={handleInputChange}
        className="text-[13px] px-2 py-2  bg-transparent border-none focus:outline-none text-gray-800 cursor-pointer rounded-l-xl"
        disabled={isLoading}
        aria-label="Country Code"
      >
        <option  value="+91">🇮🇳 +91</option>
      </select>

      <div className="w-px bg-gray-200 my-1" />

      <input
        id="phoneNumber"
        type="tel"
        name="phoneNumber"
        placeholder="Enter Phone Number"
        value={formData.phoneNumber}
        onChange={(e) => {
          const value = e.target.value.replace(/[^0-9]/g, "");
          if (value.length <= 10) {
            setFormData((prev) => ({ ...prev, phoneNumber: value }));
          }
        }}
        className="flex-1 text-[13px] px-2 py-2  border-none focus:outline-none text-gray-800 bg-transparent rounded-r-xl"
        required
        maxLength={10}
        inputMode="numeric"
        pattern="[0-9]{10}"
        title="Please enter a valid 10-digit mobile number"
        disabled={isLoading}
      />
    </div>
  </div>

  {/* Location */}
  <div className="flex-1">
    <label
      htmlFor="location"
      className="block mb-1 text-sm font-semibold text-gray-700"
    >
     Select Location <span className="text-red-500">*</span>
    </label>
    <select
      id="location"
      value={selectedLocationId ? String(selectedLocationId) : ""}
      required
      onChange={(e) => {
        const value = e.target.value;
        const loc = locations.find((l) => String(l.LocationId) === value);

        setIsLocationManuallyChanged(true);

        if (loc) {
          setSelectedLocationId(loc.LocationId);
          setSelectedLocationName(loc.LocationName);
        } else {
          setSelectedLocationId(null);
          setSelectedLocationName("");
        }
      }}
      className="w-full text-[13px] px-2 py-2  rounded-xl border-2 border-gray-200 bg-white shadow-sm focus:border-[#184A84] focus:outline-none"
    >
      <option value="">Select Location</option>
      {locations.map((loc) => (
        <option key={loc.LocationId} value={loc.LocationId}>
          {loc.LocationName}
        </option>
      ))}
    </select>
  </div>

  {/* Submit Button */}
  <div className="lg:flex-shrink-0 flex items-end">
    <button
      type="submit"
      disabled={
        !formData.fullName ||
        !formData.phoneNumber ||
          !selectedLocationId ||
        !isAgreed ||
        isLoading
      }
      className={`w-full lg:w-auto px-8 py-3 text-white font-bold rounded-xl shadow-md transition-all duration-300 flex items-center justify-center gap-2 ${
        !formData.fullName ||
        !formData.phoneNumber ||
            !selectedLocationId ||
        !isAgreed ||
        isLoading
          ? "bg-gray-400 cursor-not-allowed"
          : "bg-[#184A84] hover:shadow-lg hover:bg-[#135a75]"
      }`}
    >
      {isLoading ? (
        <>
          <svg
            className="animate-spin h-4 w-4 text-white"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Submitting...
        </>
      ) : (
        "Submit"
      )}
    </button>
  </div>
</div>

            {/* ── Remark textarea — AB requests only ── */}
            {isABRequest && (
              <div className="w-full">
                <textarea
                  name="remark"
                  value={formData.remark}
                  onChange={handleInputChange}
                  placeholder="Please describe what you're looking for (e.g. doctor type, symptoms, preferred timing)..."
                  rows={3}
                  disabled={isLoading}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#184A84] focus:outline-none text-gray-800 bg-white shadow-sm transition-all duration-300 resize-none text-sm"
                />
               
              </div>
            )}

            {/* Checkbox */}
            <div className="flex items-start gap-3 rounded-xl p-3 bg-white border border-gray-200">
              <input
                type="checkbox"
                id="whatsappUpdates"
                checked={isAgreed}
                onChange={(e) => setIsAgreed(e.target.checked)}
                className="w-5 h-5 text-[#184A84] border-gray-300 rounded cursor-pointer mt-0.5 flex-shrink-0"
                disabled={isLoading}
              />
              <label
                htmlFor="whatsappUpdates"
                className="text-gray-700 text-sm leading-relaxed cursor-pointer select-none flex-1"
              >
                Get Updates on WhatsApp. I agree to the{" "}
                <Link
                  to="/terms_conditions"
                  className="text-[#009A85] font-semibold hover:underline"
                >
                  T&C
                </Link>
                .
              </label>
            </div>

            {/* Trust Badges */}
            <div className="flex items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2 text-gray-500">
                <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Secure & Private</span>
              </div>
              <div className="flex items-center gap-2 text-gray-500">
                <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                <span>Quick Response</span>
              </div>
            </div>

          </form>
        </div>
      </div>
    </>
  );
};